import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import ResmoMascot from "../components/ResmoMascot";
import { ArrowLeft } from "lucide-react";

function Column({ doc, label, color }) {
  if (!doc) return null;
  const r = doc.result || {};
  const fit = r.fit_assessment || {};
  const tailored = r.tailored_resume || {};
  return (
    <div className="rm-card-static p-6 md:p-8" style={{ background: color || "white" }} data-testid={`compare-col-${label}`}>
      <div className="rm-tag mb-3">{label}</div>
      <h2 className="font-display text-2xl uppercase leading-tight">{doc.job_title || "—"}</h2>
      <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
        @ {doc.company_name || "—"} · {doc.created_at ? new Date(doc.created_at).toLocaleString() : ""}
      </div>
      {doc.tone_hint && (
        <div className="mt-3 p-3 rounded-md" style={{ background: "white", border: "3px solid #0F1B2D" }}>
          <div className="rm-label mb-1">Tone hint</div>
          <div className="text-sm">{doc.tone_hint}</div>
        </div>
      )}

      <div className="mt-5 flex items-center gap-3">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center font-display text-2xl"
          style={{
            background: (fit.overall_score || 0) >= 75 ? "#10B981" : (fit.overall_score || 0) >= 50 ? "#FFC145" : "#EF4444",
            border: "3px solid #0F1B2D",
            color: "white",
          }}
        >
          {Math.round(fit.overall_score || 0)}
        </div>
        <div className="flex-1 text-sm">{fit.verdict || "—"}</div>
      </div>

      <div className="mt-5">
        <div className="rm-label mb-2">Tailored summary</div>
        <p className="text-sm" data-testid={`compare-summary-${label}`}>
          {tailored.summary || "—"}
        </p>
      </div>

      <div className="mt-5">
        <div className="rm-label mb-2">Headline</div>
        <p className="font-display text-lg">{tailored.headline || "—"}</p>
      </div>

      {(tailored.key_skills || []).length > 0 && (
        <div className="mt-5">
          <div className="rm-label mb-2">Key skills</div>
          <div className="flex flex-wrap gap-2">
            {tailored.key_skills.map((s, i) => (
              <span key={i} className="rm-tag" style={{ background: "white" }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {(tailored.experience || []).length > 0 && (
        <div className="mt-5">
          <div className="rm-label mb-2">Experience</div>
          <div className="space-y-3">
            {tailored.experience.map((exp, i) => (
              <div key={i} className="p-3 rounded-md" style={{ border: "3px solid #0F1B2D", background: "white" }}>
                <div className="font-bold text-sm">
                  {exp.title} <span className="font-normal" style={{ color: "#475569" }}>— {exp.company}</span>
                </div>
                <ul className="mt-2 space-y-1 text-sm">
                  {(exp.bullets || []).map((b, j) => (
                    <li key={j}>▸ {b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5">
        <Link to={`/dashboard/${doc.id}`} className="rm-btn rm-btn-ghost text-xs px-3 py-2" data-testid={`compare-open-${label}`}>
          Open full view →
        </Link>
      </div>
    </div>
  );
}

export default function Compare() {
  const { idA, idB } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analyses/compare", { params: { a: idA, b: idB } });
        setData(res.data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [idA, idB]);

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <button onClick={() => navigate("/history")} className="rm-tag mb-3" data-testid="compare-back-btn">
          <ArrowLeft size={14} /> History
        </button>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-3">
          Side-by-side <span className="rm-text-orange">face-off.</span>
        </h1>
        <p className="text-base mb-8" style={{ color: "#475569" }}>
          Two takes, same job. Pick the lines you like best.
        </p>

        {error && (
          <div className="p-4 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="compare-error">
            {error}
          </div>
        )}

        {!data ? (
          <div className="text-center py-16" data-testid="compare-loading">
            <ResmoMascot state="digesting" size={160} />
            <div className="font-display text-2xl uppercase mt-4">Lining them up...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="compare-grid">
            <Column doc={data.a} label="Original" color="white" />
            <Column doc={data.b} label="Re-tailored" color="#FFFBEB" />
          </div>
        )}
      </div>
    </div>
  );
}
